import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

interface ListRowProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Leading visual (icon chip, avatar…). */
  leading?: ReactNode;
  /** Trailing content; defaults to a chevron when the row is clickable. */
  trailing?: ReactNode;
  onClick?: () => void;
  /** Hide the default chevron on clickable rows. */
  hideChevron?: boolean;
  destructive?: boolean;
  className?: string;
}

/**
 * Settings-style row. Renders a real <button> when clickable so it is
 * keyboard- and screen-reader-friendly. Min height 56px.
 */
export function ListRow({
  title,
  subtitle,
  leading,
  trailing,
  onClick,
  hideChevron = false,
  destructive = false,
  className,
}: ListRowProps) {
  const body = (
    <>
      {leading}
      <span className="flex-1 min-w-0 text-left">
        <span
          className={cn(
            "block font-semibold truncate",
            destructive ? "text-destructive" : "text-foreground"
          )}
        >
          {title}
        </span>
        {subtitle && (
          <span className="block text-[13px] text-muted-foreground truncate">{subtitle}</span>
        )}
      </span>
      {trailing ??
        (onClick && !hideChevron ? (
          <ChevronRight className="w-5 h-5 text-muted-foreground/70 shrink-0" aria-hidden="true" />
        ) : null)}
    </>
  );

  const base = "w-full flex items-center gap-3 px-4 py-3 min-h-[56px]";

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          base,
          "transition-colors hover:bg-surface active:bg-surface-raised",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          className
        )}
      >
        {body}
      </button>
    );
  }

  return <div className={cn(base, className)}>{body}</div>;
}

interface ListGroupProps {
  children: ReactNode;
  /** Optional heading rendered above the group. */
  title?: string;
  className?: string;
}

/** Rounded card grouping several ListRows with hairline dividers. */
export function ListGroup({ children, title, className }: ListGroupProps) {
  return (
    <section className={className}>
      {title && (
        <h2 className="px-1 mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </h2>
      )}
      <div className="card-surface overflow-hidden divide-y divide-border">{children}</div>
    </section>
  );
}

interface IconChipProps {
  children: ReactNode;
  tone?: "neutral" | "primary" | "purple" | "danger" | "success" | "warning";
  className?: string;
}

const CHIP_TONES = {
  neutral: "bg-surface-raised text-foreground",
  primary: "bg-accent text-accent-foreground",
  purple: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
  danger: "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300",
  success: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  warning: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
} as const;

/** Round icon container used as a ListRow leading visual. */
export function IconChip({ children, tone = "neutral", className }: IconChipProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0",
        CHIP_TONES[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

export default ListRow;
